import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { getEstimateListByPage } from '../api/estimate';
import PageTitle from '../components/layout/PageTitle';
import { ESTIMATE_TYPES } from '../const/estimateOptions';

const HEAD_CELLS = [
  { key: 'id', label: '순번', mobile: true, width: 60 },
  { key: 'estimateType', label: '구분', mobile: true, width: 70 },
  { key: 'name', label: '등록자', mobile: true },
  { key: 'travelType', label: '여행구분', mobile: false },
  { key: 'departPlace', label: '출발지', mobile: false },
  { key: 'arrivalPlace', label: '도착지', mobile: false },
  { key: 'vehicleType', label: '차량구분', mobile: false },
  { key: 'createdDate', label: '요청일', mobile: true },
];

/**
 * 공개 견적 목록.
 * 개인정보 보호를 위해 서버가 마스킹한 값만 내려오며, 순번 옆에 일반/통근 구분만 표시한다.
 */
export default function EstimateList() {
  const navigate = useNavigate();
  const { page } = useParams();

  const [estimateList, setEstimateList] = useState([]);
  const [allPage, setAllPage] = useState(1);

  useEffect(() => {
    document.title = '견적목록 | 이케이하나관광 버스대절';
  }, []);

  useEffect(() => {
    getEstimateListByPage(page)
      .then((response) => {
        setEstimateList(response?.estimateList ?? []);
        setAllPage(response?.totalPage ?? 1);
      })
      .catch(() => {
        setEstimateList([]);
      });
  }, [page]);

  const cellSx = (cell) => ({
    textAlign: 'center',
    fontSize: { xs: 12, md: 14 },
    px: { xs: 0.5, md: 2 },
    py: { xs: 1, md: 1.2 },
    whiteSpace: 'nowrap',
    display: cell.mobile ? 'table-cell' : { xs: 'none', md: 'table-cell' },
  });

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 1.5, md: 3 }, py: { xs: 2, md: 4 } }}>
      <PageTitle title='견적 목록' />

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ mb: 2, alignItems: { sm: 'center' }, justifyContent: 'space-between' }}
      >
        <Typography sx={{ fontSize: { xs: 13, md: 14 }, color: '#6b5544', wordBreak: 'keep-all' }}>
          고객 개인정보 보호를 위해 요청 내용은 표시하지 않습니다. 본인 견적은 [나의견적확인]에서
          휴대폰 문자 인증으로 확인하실 수 있습니다.
        </Typography>
        <Button
          variant='contained'
          onClick={() => navigate('/my')}
          sx={{
            backgroundColor: '#1B3C7A',
            color: '#fff',
            fontWeight: 800,
            fontSize: 15,
            px: 3,
            py: 1.1,
            whiteSpace: 'nowrap',
            boxShadow: '0 3px 10px rgba(27, 60, 122, 0.28)',
            '&:hover': { backgroundColor: '#122C5C' },
          }}
        >
          나의견적확인
        </Button>
      </Stack>

      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
        <Table size='small'>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#FFFACB' }}>
              {HEAD_CELLS.map((cell) => (
                <TableCell
                  key={cell.key}
                  sx={{ ...cellSx(cell), fontWeight: 700, color: '#5A4231', width: cell.width }}
                >
                  {cell.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {estimateList.length > 0 ? (
              estimateList.map((data) => (
                <TableRow key={data.id} hover>
                  <TableCell sx={cellSx(HEAD_CELLS[0])}>{data.id}</TableCell>
                  <TableCell sx={cellSx(HEAD_CELLS[1])}>
                    <Chip
                      size='small'
                      label={data.estimateType === ESTIMATE_TYPES.COMMUTE ? '통근' : '일반'}
                      sx={{
                        height: 22,
                        fontSize: 11,
                        fontWeight: 700,
                        color: '#FCFCFC',
                        backgroundColor:
                          data.estimateType === ESTIMATE_TYPES.COMMUTE ? '#42772F' : '#EC9F46',
                      }}
                    />
                  </TableCell>
                  <TableCell sx={cellSx(HEAD_CELLS[2])}>{data.name}</TableCell>
                  <TableCell sx={cellSx(HEAD_CELLS[3])}>{data.travelType}</TableCell>
                  <TableCell sx={cellSx(HEAD_CELLS[4])}>{data.departPlace}</TableCell>
                  <TableCell sx={cellSx(HEAD_CELLS[5])}>{data.arrivalPlace}</TableCell>
                  <TableCell sx={cellSx(HEAD_CELLS[6])}>{data.vehicleType}</TableCell>
                  <TableCell sx={cellSx(HEAD_CELLS[7])}>
                    {String(data.createdDate ?? '').substring(0, 10)}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={HEAD_CELLS.length} sx={{ textAlign: 'center', py: 5 }}>
                  견적 요청 내역이 없습니다.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack sx={{ mt: 3, alignItems: 'center' }}>
        <Pagination
          count={allPage}
          page={Number(page) || 1}
          shape='rounded'
          size='small'
          onChange={(event, value) => navigate(`/list/${value}`)}
        />
      </Stack>
    </Box>
  );
}
